/**
 * The relayer's Solana settlement route, reached through the same-origin
 * engine proxy that attaches the V2 HMAC. The body is the SDK intent and
 * nothing else; the relayer re-verifies the proof and decides how to submit.
 */
import type { SolanaSettlementIntent } from "@stealthos/sdk";
import { withReadCancellation } from "../readCancellation";
import { withDeadline } from "../../utils/withDeadline";
import {
  validateSolanaSwapQuote,
  type SolanaSwapRequest,
  type SolanaSwapQuote,
} from "./swapQuote";
import {
  validateSolanaFeeQuote,
  type SolanaFeeQuote,
  type SolanaFeeRequest,
} from "./fees";

export interface SolanaRelayResponse {
  success: boolean;
  requestId?: string;
  replayed?: boolean;
  network?: string;
  kind?: string;
  mode?: string;
  settleSignature: string;
  stagingSignatures?: string[];
  lookupTable?: string | null;
  unitsConsumed?: number | null;
  requiredFee?: string | null;
}

export class SolanaRelayError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly submissionNotAttempted: boolean;
  readonly settlementNotSubmitted: boolean;

  constructor(
    status: number,
    code: string | null,
    detail: string,
    submissionNotAttempted = false,
    settlementNotSubmitted = false,
  ) {
    super(detail);
    this.name = "SolanaRelayError";
    this.status = status;
    this.code = code;
    this.submissionNotAttempted = submissionNotAttempted;
    this.settlementNotSubmitted = settlementNotSubmitted;
  }
}

export interface SolanaRelayClient {
  publishRecovery?(
    request: {
      network: string;
      programId: string;
      records: { kind: 1 | 2 | 3; locator: string; payload: string }[];
    },
    options?: { signal?: AbortSignal },
  ): Promise<{ signatures: string[] }>;
  recover?(
    intent: SolanaSettlementIntent,
    options?: { signal?: AbortSignal },
  ): Promise<{
    status: "pending" | "closed" | "settled";
    signature?: string;
    reason?: string;
  }>;
  quoteSwap?(
    request: SolanaSwapRequest,
    options?: { signal?: AbortSignal },
  ): Promise<SolanaSwapQuote>;
  quote(
    request: SolanaFeeRequest,
    options?: { signal?: AbortSignal },
  ): Promise<SolanaFeeQuote>;
  settle(
    intent: SolanaSettlementIntent,
    options: { idempotencyKey: string; signal?: AbortSignal },
  ): Promise<SolanaRelayResponse>;
}

function relayUrl(engineProxyUrl: string, relayPath: string): string {
  const base = engineProxyUrl.replace(/\/+$/, "");
  return base.includes("?path=")
    ? `${base}${relayPath.replace(/^\//, "")}`
    : `${base}${relayPath}`;
}

function describe(status: number, code: string | null, error: string): string {
  switch (code) {
    case "TX_005":
      return "another attempt to settle these notes is in flight; wait for it and refresh";
    case "VAL_002":
      return "the relayer does not serve this Solana network";
    case "INT_003":
      return "the relayer is not configured for Solana settlements";
    case "FEE_001":
      return `the relayer refused the fee: ${error}`;
    default:
      return status === 401 || status === 403
        ? "the relay session was rejected; sign in again"
        : `relayer refused the settlement (${status}${code ? ` ${code}` : ""}): ${error}`;
  }
}

export function createSolanaRelayClient(params: {
  engineProxyUrl: string;
  relayPath: string;
  authHeaders: () => Promise<Record<string, string>>;
  fetchImpl?: typeof fetch;
}): SolanaRelayClient {
  const url = relayUrl(params.engineProxyUrl, params.relayPath);
  const fetchImpl = params.fetchImpl ?? ((input, init) => fetch(input, init));
  return {
    async publishRecovery(request, options = {}) {
      return withDeadline(
        async (signal) => {
          const response = await fetchImpl(`${url}/recovery-records`, {
            method: "POST",
            credentials: "same-origin",
            cache: "no-store",
            redirect: "error",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              ...(await params.authHeaders()),
            },
            body: JSON.stringify(request),
            signal,
          });
          const body = await response.json();
          if (
            !response.ok ||
            body?.success !== true ||
            !Array.isArray(body.signatures) ||
            body.signatures.some(
              (signature: unknown) => typeof signature !== "string",
            )
          )
            throw new Error(
              "On-chain recovery publication failed; transaction was not submitted",
            );
          return { signatures: body.signatures };
        },
        "On-chain recovery publication timed out; transaction was not submitted",
        options.signal,
        90_000,
      );
    },
    async recover(intent, options = {}) {
      return withDeadline(
        async (signal) => {
          const response = await fetchImpl(`${url}/recover`, {
            method: "POST",
            credentials: "same-origin",
            cache: "no-store",
            redirect: "error",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              ...(await params.authHeaders()),
            },
            body: JSON.stringify(intent),
            signal,
          });
          const body = await response.json();
          if (
            !response.ok ||
            body?.success !== true ||
            !["pending", "closed", "settled"].includes(body.status) ||
            (body.status === "settled" &&
              (typeof body.signature !== "string" || !body.signature))
          )
            throw new Error(
              typeof body?.error === "string"
                ? body.error
                : "Relayer recovery returned an invalid result",
            );
          return body;
        },
        "Relayer recovery timed out; your saved transaction is preserved",
        options.signal,
        30_000,
      );
    },
    async quoteSwap(request, options = {}) {
      const response = await fetchImpl(`${url}/quote`, {
        method: "POST",
        credentials: "same-origin",
        cache: "no-store",
        redirect: "error",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(await params.authHeaders()),
        },
        body: JSON.stringify(request),
        signal: options.signal,
      });
      const body = await response.json();
      if (!response.ok || body?.success !== true)
        throw new Error(
          typeof body?.error === "string"
            ? body.error
            : "Orca quote unavailable. Try again.",
        );
      return validateSolanaSwapQuote(body.quote, request);
    },
    async quote(request, options = {}) {
      const response = await fetchImpl(`${url}/quote`, {
        method: "POST",
        credentials: "same-origin",
        cache: "no-store",
        redirect: "error",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(await params.authHeaders()),
        },
        body: JSON.stringify(request),
        signal: options.signal,
      });
      const body = await response.json();
      if (!response.ok || body?.success !== true)
        throw new Error(
          typeof body?.error === "string"
            ? body.error
            : "Solana fee quote unavailable. Try again.",
        );
      return validateSolanaFeeQuote(body.quote, request);
    },
    async settle(intent, options) {
      const authHeaders = await withReadCancellation(
        params.authHeaders,
        options.signal,
      );
      const response = await fetchImpl(url, {
        method: "POST",
        credentials: "same-origin",
        cache: "no-store",
        redirect: "error",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "Idempotency-Key": options.idempotencyKey,
          ...authHeaders,
        },
        body: JSON.stringify(intent),
        signal: options.signal,
      });
      let body: Record<string, unknown> | null = null;
      try {
        body = (await response.json()) as Record<string, unknown>;
      } catch {
        body = null;
      }
      if (!response.ok || body?.success !== true) {
        const code = typeof body?.code === "string" ? body.code : null;
        const error =
          typeof body?.error === "string"
            ? body.error
            : typeof body?.message === "string"
              ? body.message
              : `HTTP ${response.status}`;
        throw new SolanaRelayError(
          response.status,
          code,
          describe(response.status, code, error),
          (body?.details as { submissionNotAttempted?: unknown } | undefined)
            ?.submissionNotAttempted === true,
          (body?.details as { settlementNotSubmitted?: unknown } | undefined)
            ?.settlementNotSubmitted === true,
        );
      }
      if (typeof body.settleSignature !== "string" || !body.settleSignature) {
        throw new SolanaRelayError(
          response.status,
          null,
          "relayer reported success without a settlement signature",
        );
      }
      return body as unknown as SolanaRelayResponse;
    },
  };
}
